import { Component, type ErrorInfo, type ReactNode } from 'react';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

/**
 * Top-level safety net so a render failure anywhere in the routed app shows a recoverable
 * panel (with the captured error + component stack) instead of a blank white page.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    if (typeof console !== 'undefined') {
      console.error('[app] uncaught render error', error, info.componentStack);
    }
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div role="alert" style={panelStyle}>
        <div style={cardStyle}>
          <h1 style={{ margin: '0 0 8px', fontSize: 18 }}>Something went wrong</h1>
          <p style={{ margin: '0 0 12px', color: '#555' }}>
            The page hit an unexpected error and was stopped to avoid a blank screen. Reload to try
            again. If it keeps happening, copy the details below and send them over.
          </p>
          <button type="button" onClick={this.handleReload} style={buttonStyle}>
            Reload page
          </button>
          <pre style={detailsStyle}>
            {error.message}
            {componentStack ? `\n\nComponent stack:${componentStack}` : ''}
          </pre>
        </div>
      </div>
    );
  }
}

const panelStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: 24,
  background: '#f4f5f7',
  boxSizing: 'border-box' as const
};

const cardStyle = {
  maxWidth: 720,
  width: '100%',
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  padding: 20,
  fontFamily: 'system-ui, sans-serif'
};

const buttonStyle = {
  padding: '8px 16px',
  border: 'none',
  borderRadius: 6,
  background: '#2563eb',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 14
};

const detailsStyle = {
  marginTop: 16,
  padding: 12,
  background: '#fafafa',
  border: '1px solid #eee',
  borderRadius: 6,
  fontSize: 12,
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
  maxHeight: 320,
  overflow: 'auto'
};
